import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  ChatBubbleOutline as SummaryIcon,
  HourglassTop as QueueIcon,
  MonitorHeart as VitalsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AuthShell from '../components/AuthShell';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function formatLoginError(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  const detailStr =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d) => (typeof d === 'object' && d?.msg ? d.msg : String(d))).join(' ')
        : '';

  if (status === 401 || status === 403) {
    const lower = detailStr.toLowerCase();
    if (
      !detailStr ||
      lower.includes('invalid') ||
      lower.includes('credential') ||
      lower.includes('incorrect')
    ) {
      return 'Incorrect email or password. Check that both are correct and try again.';
    }
    return detailStr;
  }
  if (detailStr) return detailStr;
  if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
    return `Cannot reach ${API_URL}. Start the backend on port 8000, or set REACT_APP_API_URL. Using a LAN IP? Add that URL to CORS_ORIGINS in server/backend/.env.`;
  }
  if (err.message) return err.message;
  return 'Login failed';
}

const AMBER = '#fbbf24';

// What a patient actually gets after a visit: a note in plain words and
// their place in the queue. Illustrative only — no real names or records.
function VisitPreview() {
  return (
    <Box sx={{ display: 'grid', gap: 1.5, maxWidth: 460 }}>
      <Box
        sx={{
          p: 2.25,
          borderRadius: '16px',
          bgcolor: '#fffaf3',
          color: '#431407',
          boxShadow: '0 30px 60px -20px rgba(0,0,0,0.45)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8, color: '#b45309' }}>
            YOUR VISIT SUMMARY
          </Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#a8a29e' }}>Today</Typography>
        </Box>
        <Typography sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 20, fontWeight: 700, lineHeight: 1.25, mb: 0.75 }}>
          Your heart rhythm looked normal.
        </Typography>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: '#78716c' }}>
          The fluttering is most likely from caffeine. Try cutting back to one coffee a day and
          let us know if it comes back.
        </Typography>
      </Box>
      <Box
        sx={{
          alignSelf: 'start',
          justifySelf: 'end',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 1,
          borderRadius: 99,
          bgcolor: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.18)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box
          sx={{
            width: 8, height: 8, borderRadius: '50%', bgcolor: AMBER,
            animation: 'livePulse 1.8s ease-in-out infinite',
            '@keyframes livePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
          }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>You’re next in line</Typography>
      </Box>
    </Box>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    '&.Mui-focused fieldset': { borderWidth: '2px' },
  },
};

export default function Login() {
  const navigate = useNavigate();
  const { unifiedLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { routed } = await unifiedLogin(email, password);
      if (routed) return;   // home portal is elsewhere — redirecting there
      navigate('/');
    } catch (err) {
      setError(formatLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      portalName="Patient portal"
      panelBackground="linear-gradient(155deg, #2a0d04 0%, #5c1d08 50%, #92400e 100%)"
      accent={AMBER}
      headlineFont='"Cormorant Garamond", Georgia, serif'
      eyebrow="Your care, in one place"
      headline={<>Your visits,<br /><Box component="span" sx={{ color: AMBER }}>in plain language.</Box></>}
      body="See what your doctor found, what happens next, and where you are in the queue — without the medical jargon."
      points={[
        { icon: <SummaryIcon />, title: 'Visit summaries you can read', text: 'Every visit explained in everyday words, right after it ends.' },
        { icon: <QueueIcon />, title: 'Live queue on your phone', text: 'Wait where you like — we’ll show you when it’s your turn.' },
        { icon: <VitalsIcon />, title: 'Your health over time', text: 'Vitals and past visits kept together, ready to share.' },
      ]}
      preview={<VisitPreview />}
    >
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.2 }}>
        Patient portal
      </Typography>
      <Typography variant="h4" component="h2" sx={{ mt: 0.5, mb: 1, fontSize: 38 }}>
        Welcome back
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Sign in to see your visits and health record.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          autoComplete="email"
          InputLabelProps={{ shrink: true }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          sx={fieldSx}
        />
        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          InputLabelProps={{ shrink: true }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  // Announced rather than left as a bare icon, and never a tab
                  // stop between the password and the submit button.
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <HideIcon /> : <ShowIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Link component="button" type="button" underline="hover" onClick={() => navigate('/forgot')} sx={{ fontWeight: 600, fontSize: 14 }}>
            Forgot password?
          </Link>
        </Box>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            mt: 2.5,
            py: 1.5,
            background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
            fontWeight: 700,
            fontSize: '1rem',
            boxShadow: '0 4px 14px 0 rgba(180, 83, 9, 0.39)',
            '&:hover': {
              background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
              boxShadow: '0 6px 20px 0 rgba(180, 83, 9, 0.45)',
            },
          }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <Box sx={{ mt: 3, p: 2, borderRadius: '12px', bgcolor: 'rgba(180, 83, 9, 0.06)', border: '1px solid rgba(180, 83, 9, 0.14)' }}>
        <Typography variant="body2" color="text.secondary">
          Got an invite from your clinic?{' '}
          <Link component="button" type="button" underline="hover" onClick={() => navigate('/register')} sx={{ fontWeight: 700, verticalAlign: 'baseline' }}>
            Set up your account
          </Link>
        </Typography>
      </Box>
    </AuthShell>
  );
}
